import { useCallback, useEffect, useState } from "react";
import {
  TREATMENT_PHASES,
  type TreatmentPhase,
  type WeeklyThemeDto,
  type WeeklyThemeUpsert,
} from "@care-by-ella/contracts";
import { useAuth } from "@/auth/AuthContext";
import { UnauthorizedError } from "@/lib/clinician-api";
import {
  listWeeklyThemes,
  publishWeeklyTheme,
  unpublishWeeklyTheme,
  upsertWeeklyTheme,
} from "@/lib/weekly-theme-api";

// TES-359 — veckotema + "För dig idag" authoring. One theme per derived
// treatment phase; title/body feed Home §1, forYouTitle/forYouBody feed §5 —
// ONE row drives both, so the shared-focus invariant holds. Publish makes the
// row the /home-summary source for its phase; unpublish falls back to the
// built-in copy. Mirrors Wellness.tsx (inline editor per row).

type Draft = {
  titleSv: string;
  titleEn: string;
  bodySv: string;
  bodyEn: string;
  forYouTitleSv: string;
  forYouTitleEn: string;
  forYouBodySv: string;
  forYouBodyEn: string;
  ctaLabelSv: string;
  ctaLabelEn: string;
  ctaPath: string;
  status: WeeklyThemeDto["status"] | "new";
};

const EMPTY: Draft = {
  titleSv: "",
  titleEn: "",
  bodySv: "",
  bodyEn: "",
  forYouTitleSv: "",
  forYouTitleEn: "",
  forYouBodySv: "",
  forYouBodyEn: "",
  ctaLabelSv: "",
  ctaLabelEn: "",
  ctaPath: "",
  status: "new",
};

function toDraft(t: WeeklyThemeDto): Draft {
  return {
    titleSv: t.title.sv,
    titleEn: t.title.en,
    bodySv: t.body.sv,
    bodyEn: t.body.en,
    forYouTitleSv: t.forYouTitle.sv,
    forYouTitleEn: t.forYouTitle.en,
    forYouBodySv: t.forYouBody.sv,
    forYouBodyEn: t.forYouBody.en,
    ctaLabelSv: t.ctaLabel?.sv ?? "",
    ctaLabelEn: t.ctaLabel?.en ?? "",
    ctaPath: t.ctaPath ?? "",
    status: t.status,
  };
}

const PHASE_LABELS: Record<TreatmentPhase, string> = {
  pre_treatment: "Förberedelse (pre_treatment)",
  start_phase: "Startfas (start_phase)",
  dose_adjustment_phase: "Dosjustering (dose_adjustment_phase)",
  active_weight_loss: "Aktiv viktnedgång (active_weight_loss)",
  stabilisation: "Stabilisering (stabilisation)",
  maintenance: "Underhåll (maintenance)",
  paused: "Paus (paused)",
  ended: "Avslutad (ended)",
};

export function WeeklyThemesRoute() {
  const { signOut } = useAuth();
  const [drafts, setDrafts] = useState<Record<string, Draft> | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const handleErr = useCallback(
    (e: unknown) => {
      if (e instanceof UnauthorizedError) {
        void signOut();
        return;
      }
      setError(e instanceof Error ? e.message : String(e));
    },
    [signOut],
  );

  const load = useCallback(() => {
    setError(null);
    listWeeklyThemes()
      .then((entries) => {
        const next: Record<string, Draft> = {};
        for (const entry of entries) {
          next[entry.phaseKey] = entry.theme
            ? toDraft(entry.theme)
            : { ...EMPTY };
        }
        setDrafts(next);
      })
      .catch(handleErr);
  }, [handleErr]);

  useEffect(load, [load]);

  function patch(phase: string, p: Partial<Draft>) {
    setDrafts((d) => (d ? { ...d, [phase]: { ...d[phase]!, ...p } } : d));
  }

  function payload(phase: TreatmentPhase, draft: Draft): WeeklyThemeUpsert {
    const status = draft.status === "new" ? "draft" : draft.status;
    // CTA is all-or-nothing: only send it when label (both locales) + path
    // are present, matching the contract's paired refine.
    const hasCta =
      draft.ctaLabelSv.trim() &&
      draft.ctaLabelEn.trim() &&
      draft.ctaPath.trim();
    return {
      phaseKey: phase,
      title: { sv: draft.titleSv, en: draft.titleEn },
      body: { sv: draft.bodySv, en: draft.bodyEn },
      forYouTitle: { sv: draft.forYouTitleSv, en: draft.forYouTitleEn },
      forYouBody: { sv: draft.forYouBodySv, en: draft.forYouBodyEn },
      ctaLabel: hasCta ? { sv: draft.ctaLabelSv, en: draft.ctaLabelEn } : null,
      ctaPath: hasCta ? draft.ctaPath.trim() : null,
      status,
    };
  }

  // A partially-filled CTA (some but not all of sv label / en label / path)
  // would otherwise be silently dropped by payload()'s all-or-nothing gate.
  function ctaPartial(draft: Draft): boolean {
    const parts = [
      draft.ctaLabelSv.trim(),
      draft.ctaLabelEn.trim(),
      draft.ctaPath.trim(),
    ];
    const filled = parts.filter(Boolean).length;
    return filled > 0 && filled < parts.length;
  }

  async function run(phase: TreatmentPhase, op: () => Promise<WeeklyThemeDto>) {
    setBusy(phase);
    setError(null);
    setNotice(null);
    try {
      const saved = await op();
      patch(phase, toDraft(saved));
      setNotice("Sparat.");
    } catch (e) {
      handleErr(e);
    } finally {
      setBusy(null);
    }
  }

  if (!drafts) {
    return <div className="text-textSecondary text-sm">Hämtar…</div>;
  }

  const field =
    "border border-surfaceMuted rounded-md p-sm text-sm focus:outline-none focus:border-brand";
  const labelCls = "text-xs uppercase tracking-wide text-textSecondary";

  return (
    <div className="flex flex-col gap-md max-w-3xl">
      <h1 className="font-display text-3xl tracking-tight">Veckotema</h1>
      <p className="text-sm text-textSecondary max-w-2xl">
        Ett tema per behandlingsfas: hälsningen (§1) och &quot;För dig
        idag&quot; (§5) på appens hemskärm delar alltid samma fokus. Bara
        publicerade teman visas för patienten — faser utan publicerat tema
        använder den inbyggda texten.
      </p>

      {error ? (
        <div className="bg-danger/10 border border-danger/30 text-danger rounded-md p-sm text-sm">
          {error}
        </div>
      ) : null}
      {notice ? (
        <div className="bg-success/10 border border-success/30 text-success rounded-md p-sm text-sm">
          {notice}
        </div>
      ) : null}

      {TREATMENT_PHASES.map((phase) => {
        const draft = drafts[phase]!;
        const isNew = draft.status === "new";
        return (
          <div
            key={phase}
            className="bg-surface border border-surfaceMuted rounded-md p-md flex flex-col gap-sm"
          >
            <div className="flex items-baseline justify-between">
              <h2 className="font-display text-lg">{PHASE_LABELS[phase]}</h2>
              <span className="text-xs text-textSecondary">
                {isNew ? "inbyggd text" : `status: ${draft.status}`}
              </span>
            </div>

            <div className="grid grid-cols-2 gap-sm">
              <label className="flex flex-col gap-xs">
                <span className={labelCls}>Titel (sv)</span>
                <input
                  value={draft.titleSv}
                  onChange={(e) => patch(phase, { titleSv: e.target.value })}
                  className={field}
                />
              </label>
              <label className="flex flex-col gap-xs">
                <span className={labelCls}>Title (en)</span>
                <input
                  value={draft.titleEn}
                  onChange={(e) => patch(phase, { titleEn: e.target.value })}
                  className={field}
                />
              </label>
              <label className="flex flex-col gap-xs">
                <span className={labelCls}>Text (sv)</span>
                <textarea
                  value={draft.bodySv}
                  onChange={(e) => patch(phase, { bodySv: e.target.value })}
                  className={`${field} min-h-[56px]`}
                />
              </label>
              <label className="flex flex-col gap-xs">
                <span className={labelCls}>Body (en)</span>
                <textarea
                  value={draft.bodyEn}
                  onChange={(e) => patch(phase, { bodyEn: e.target.value })}
                  className={`${field} min-h-[56px]`}
                />
              </label>
            </div>

            <div className="flex flex-col gap-sm border-t border-surfaceMuted pt-sm">
              <span className={labelCls}>För dig idag (§5)</span>
              <div className="grid grid-cols-2 gap-sm">
                <label className="flex flex-col gap-xs">
                  <span className={labelCls}>Rubrik (sv)</span>
                  <input
                    value={draft.forYouTitleSv}
                    onChange={(e) =>
                      patch(phase, { forYouTitleSv: e.target.value })
                    }
                    className={field}
                  />
                </label>
                <label className="flex flex-col gap-xs">
                  <span className={labelCls}>Title (en)</span>
                  <input
                    value={draft.forYouTitleEn}
                    onChange={(e) =>
                      patch(phase, { forYouTitleEn: e.target.value })
                    }
                    className={field}
                  />
                </label>
                <label className="flex flex-col gap-xs">
                  <span className={labelCls}>Text (sv)</span>
                  <textarea
                    value={draft.forYouBodySv}
                    onChange={(e) =>
                      patch(phase, { forYouBodySv: e.target.value })
                    }
                    className={`${field} min-h-[56px]`}
                  />
                </label>
                <label className="flex flex-col gap-xs">
                  <span className={labelCls}>Body (en)</span>
                  <textarea
                    value={draft.forYouBodyEn}
                    onChange={(e) =>
                      patch(phase, { forYouBodyEn: e.target.value })
                    }
                    className={`${field} min-h-[56px]`}
                  />
                </label>
              </div>
            </div>

            <div className="flex flex-col gap-sm border-t border-surfaceMuted pt-sm">
              <span className={labelCls}>CTA (valfritt)</span>
              <div className="grid grid-cols-2 gap-sm">
                <label className="flex flex-col gap-xs">
                  <span className={labelCls}>CTA-text (sv)</span>
                  <input
                    value={draft.ctaLabelSv}
                    onChange={(e) =>
                      patch(phase, { ctaLabelSv: e.target.value })
                    }
                    placeholder="Läs mer"
                    className={field}
                  />
                </label>
                <label className="flex flex-col gap-xs">
                  <span className={labelCls}>CTA label (en)</span>
                  <input
                    value={draft.ctaLabelEn}
                    onChange={(e) =>
                      patch(phase, { ctaLabelEn: e.target.value })
                    }
                    placeholder="Read more"
                    className={field}
                  />
                </label>
                <label className="flex flex-col gap-xs col-span-2">
                  <span className={labelCls}>
                    CTA-mål (route eller carebyella://…)
                  </span>
                  <input
                    value={draft.ctaPath}
                    onChange={(e) => patch(phase, { ctaPath: e.target.value })}
                    placeholder="carebyella://article/..."
                    className={field}
                  />
                </label>
              </div>
            </div>

            <div className="flex items-center gap-sm">
              <button
                type="button"
                onClick={() => {
                  if (ctaPartial(draft)) {
                    setError(
                      "Fyll i CTA-text (sv + en) och CTA-mål tillsammans, eller lämna alla tomma.",
                    );
                    return;
                  }
                  void run(phase, () =>
                    upsertWeeklyTheme(phase, payload(phase, draft)),
                  );
                }}
                disabled={busy === phase}
                className="px-md py-sm rounded-md bg-brand text-white text-sm hover:bg-brand/90 disabled:opacity-50"
              >
                {busy === phase ? "Sparar…" : isNew ? "Skapa" : "Spara"}
              </button>
              {!isNew ? (
                <>
                  <button
                    type="button"
                    onClick={() => run(phase, () => publishWeeklyTheme(phase))}
                    disabled={busy === phase || draft.status === "published"}
                    className="px-md py-sm rounded-md border border-brand text-brand text-sm hover:bg-brand/5 disabled:opacity-50"
                  >
                    Publicera
                  </button>
                  <button
                    type="button"
                    onClick={() =>
                      run(phase, () => unpublishWeeklyTheme(phase))
                    }
                    disabled={busy === phase || draft.status !== "published"}
                    className="ml-auto px-md py-sm rounded-md border border-surfaceMuted text-textSecondary text-sm hover:text-danger hover:border-danger disabled:opacity-50"
                  >
                    Avpublicera
                  </button>
                </>
              ) : null}
            </div>
          </div>
        );
      })}
    </div>
  );
}
